// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { HistoryType } from '@shared/utils/History/HistoryType';
import CreateAlertWidgetTemplate from '@front/templates/CreateAlertWidgetTemplate.html';
import { Control } from '@front/controls/elements/Control';
import { ControlsTypes } from '@front/controls/UtilsClasses/FactoryConstants';
import { DataCache } from '@shared/commons/DataCache';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { ColoringEnum, Level1CalculatorUtils } from '@shared/commons/cache/Level1Utils/Level1CalculatorUtils';
import { Level1CalculatorViewModelHelper } from '@shared/commons/cache/Level1Utils/Level1CalculatorViewModelHelper';
import { VolumeFormatter } from '@shared/utils/Volume/VolumeFormatter';
export class CreateAlertWidget extends Control {
    constructor() {
        super();
    }
    getType() {
        return ControlsTypes.CreateAlertWidget;
    }
    oninit() {
        super.oninit();
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
        this.localize();
        super.observe('instrument', this.onInstrumentChanged);
        super.observe('account', this.updateValues);
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        this.unsubscribeQuotes(super.get('instrument'));
        super.dispose();
    }
    onInstrumentChanged(instrument, lastInstrument) {
        void super.set({ bidValue: -1, askValue: -1, lastValue: -1, changeValue: -1, volumeValue: -1 });
        if (instrument == null || instrument === lastInstrument) {
            return;
        }
        this.unsubscribeQuotes(lastInstrument);
        this.subscribeQuotes(instrument);
    }
    updateValues() {
        const instrument = super.get('instrument');
        if (instrument == null) {
            return;
        }
        const Level1 = instrument.Level1;
        if (Level1 == null) {
            return;
        }
        const acc = super.get('account');
        const bid = Level1.GetBid(acc);
        const ask = Level1.GetAsk(acc);
        const last = Level1.GetLastPrice(acc);
        const change = Level1.GetChangePercent(acc);
        const volume = Level1.GetVolume(); // instrument.TotalVolume;
        const bidValue = super.get('bidValue');
        this.setColouring(bid, bidValue, 'coloringBid');
        const askValue = super.get('askValue');
        this.setColouring(ask, askValue, 'coloringAsk');
        const lastValue = super.get('lastValue');
        this.setColouring(last, lastValue, 'coloringLast');
        const changeValue = super.get('changeValue');
        this.setColouring(change, changeValue, 'coloringChange');
        const volumeValue = super.get('volumeValue');
        this.setColouring(volume, volumeValue, 'coloringVolume');
        void super.set({ bidValue: bid, askValue: ask, lastValue: last, changeValue: change, volumeValue: volume });
        void super.set({
            bid_str_value: Level1.getFormatPrice(bid),
            ask_str_value: Level1.getFormatPrice(ask),
            last_str_value: Level1.getFormatPrice(last),
            change_str_value: Level1.formatChangePercentTostr(null, change),
            volume_str_value: VolumeFormatter.getVolumeFormat(volume)
        });
    }
    subscribeQuotes(instrument) {
        if (instrument == null) {
            return;
        }
        this.newQuote(instrument.LastQuote);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_TRADES);
    }
    unsubscribeQuotes(instrument) {
        if (instrument == null) {
            return;
        }
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_TRADES);
        this.newQuote(null);
    }
    newQuote(quote) {
        if (quote != null && quote.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            return;
        }
        this.updateValues();
    }
    setColouring(value, lastValue, coloringMode) {
        const coloring = Level1CalculatorUtils.getColorByDifference(lastValue, value);
        if (coloring === ColoringEnum.Previous) {
            return;
        }
        const colorClass = Level1CalculatorViewModelHelper.getDesktopColor(coloring);
        void super.set(coloringMode, colorClass);
    }
    localize() {
        void super.set('bidLabel', Resources.getResource('panel.watchlist.Bid'));
        void super.set('askLabel', Resources.getResource('panel.watchlist.Ask'));
        void super.set('lastLabel', Resources.getResource('panel.watchlist.Last'));
        void super.set('changeLabel', Resources.getResource('panel.watchlist.ChangePr'));
        void super.set('volumeLabel', Resources.getResource('panel.watchlist.Volume'));
    }
}
Control.extendWith(CreateAlertWidget, {
    template: CreateAlertWidgetTemplate,
    data: function () {
        return {
            quote: null,
            instrument: null,
            account: null,
            bidValue: NaN,
            askValue: NaN,
            lastValue: NaN,
            changeValue: NaN,
            volumeValue: NaN,
            bid_str_value: '',
            ask_str_value: '',
            last_str_value: '',
            change_str_value: '',
            volume_str_value: '',
            coloringLast: '',
            coloringChange: '',
            coloringVolume: ''
        };
    }
});
